import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { customPriceReducer } from "../../../features/posSlice";
import { notify } from "../../Utility/Notify";
import { signInUser } from "../../Utility/Auth";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api/";

const PriceSelectByProduct = ({
  sc,
  setVal,
  initialMrp,
  tp,
  article_code,
  handleOnChangeCategory,
  name,
  scValue,
  customPrice,
  compact = false,
  editable = true,
}) => {
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const originalMrpRef = useRef(
    initialMrp !== undefined && initialMrp !== null && initialMrp !== ""
      ? initialMrp
      : setVal
  );

  useEffect(() => {
    if (initialMrp !== undefined && initialMrp !== null && initialMrp !== "") {
      originalMrpRef.current = initialMrp;
    }
  }, [initialMrp]);

  const [priceList, setPriceList] = useState([]);
  const [selectCategory, setSelectCategory] = useState(scValue || "");
  const [options, setOptions] = useState([]);
  const [defaultValue, setDefaultValue] = useState({});
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(
    setVal !== undefined && setVal !== null ? setVal : ""
  );

  const auth = signInUser();

  const getCategory = async (productId) => {
    let cancelToken;

    if (typeof cancelToken !== typeof undefined) {
      cancelToken.cancel("Cancel The Previous Request");
    }

    cancelToken = axios.CancelToken.source();

    if (productId) {
      try {
        const result = await axios.get(
          `${BASE_URL}price/product/stock/${productId}`,
          {
            cancelToken: cancelToken.token,
          }
        );
        if (result?.data && Array.isArray(result.data)) {
          setPriceList(result.data);
        } else {
          setPriceList([]);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          setPriceList([]);
        }
      }
    } else {
      setPriceList([]);
    }
  };

  useEffect(() => {
    if (!editable) return;
    getCategory(sc);
    setSelectCategory(scValue || "");
  }, [sc, scValue, editable]);

  useEffect(() => {
    setInputValue(setVal !== undefined && setVal !== null ? setVal : "");
  }, [setVal]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let prices = [];
    if (priceList?.length > 0) {
      priceList.forEach((price) => {
        const vendorName = price?.supplier || price?.supplierName || "";
        prices.push({
          option: price._id,
          label: `${price?.mrp} BDT${vendorName ? ` - ${vendorName}` : ""}`,
          tp: price.tp,
          mrp: price.mrp,
          stock: price?.stock,
          supplier: price?.supplierId,
          supplierId: price?.supplierId,
          supplierName: vendorName,
        });
      });
      setOptions(prices);
      setDefaultValue(prices[0]);

      // If tp is missing or 0 on cartItem, but fetched price has tp, sync it to Redux
      if (
        (tp === undefined || tp === null || parseFloat(tp) === 0) &&
        prices[0]?.tp
      ) {
        dispatch(
          customPriceReducer({
            id: sc,
            article_code: article_code,
            e: { tp: prices[0].tp },
          })
        );
      }
    } else {
      setOptions([]);
      setDefaultValue({});
    }
  }, [priceList, sc, article_code, tp, dispatch]);

  // Selected option from options or defaultValue or first option
  const selectedBatch =
    options.find((opt) => opt.option === selectCategory) ||
    defaultValue ||
    options[0] ||
    {};

  // Determine buying price (TP / kina dam). Priority: selectedBatch.tp -> passed tp prop -> defaultValue.tp -> 0
  const effectiveTp =
    parseFloat(
      selectedBatch?.tp !== undefined && selectedBatch?.tp !== null && selectedBatch?.tp !== ""
        ? selectedBatch.tp
        : tp !== undefined && tp !== null && tp !== ""
          ? tp
          : defaultValue?.tp !== undefined
            ? defaultValue.tp
            : 0
    ) || 0;

  // Determine retail price (MRP). Priority: selectedBatch.mrp -> originalMrpRef.current -> defaultValue.mrp -> setVal -> 0
  const effectiveMrp =
    parseFloat(
      selectedBatch?.mrp !== undefined && selectedBatch?.mrp !== null && selectedBatch?.mrp !== ""
        ? selectedBatch.mrp
        : originalMrpRef.current !== undefined && originalMrpRef.current !== null && originalMrpRef.current !== ""
          ? originalMrpRef.current
          : defaultValue?.mrp !== undefined && defaultValue?.mrp !== null
            ? defaultValue.mrp
            : setVal !== undefined && setVal !== null && setVal !== ""
              ? setVal
              : 0
    ) || 0;

  const [isTpMode, setIsTpMode] = useState(false);

  useEffect(() => {
    const cur = parseFloat(inputValue);
    if (!isNaN(cur)) {
      if (effectiveTp > 0 && cur === effectiveTp && effectiveTp !== effectiveMrp) {
        setIsTpMode(true);
      } else if (cur === effectiveMrp) {
        setIsTpMode(false);
      }
    }
  }, [inputValue, effectiveTp, effectiveMrp]);

  const handleToggleMode = (targetIsTp) => {
    const targetPrice = targetIsTp ? effectiveTp : effectiveMrp;
    if (targetIsTp && effectiveTp <= 0) {
      notify("Buying price (TP) is not available for this product!", "error");
      return;
    }
    setIsTpMode(targetIsTp);
    setInputValue(targetPrice);
    dispatch(
      customPriceReducer({
        id: sc,
        article_code: article_code,
        mrp: targetPrice,
      })
    );
  };

  const handleToggleSwitch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleToggleMode(!isTpMode);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);

    if (val !== "" && !isNaN(val)) {
      const num = parseFloat(val);
      // If entered price is valid and not below buying price, dispatch in real time
      if (num >= effectiveTp) {
        if (num > effectiveTp) {
          originalMrpRef.current = num;
        }
        dispatch(
          customPriceReducer({
            id: sc,
            article_code: article_code,
            mrp: num,
          })
        );
      }
    }
  };

  const handleInputBlur = () => {
    if (inputValue === "" || isNaN(inputValue)) {
      const fallback =
        setVal !== undefined && setVal !== "" && !isNaN(setVal)
          ? parseFloat(setVal)
          : effectiveTp;
      setInputValue(fallback);
      dispatch(
        customPriceReducer({
          id: sc,
          article_code: article_code,
          mrp: fallback,
        })
      );
      return;
    }

    const num = parseFloat(inputValue);
    if (effectiveTp > 0 && num < effectiveTp) {
      notify(
        `Cannot sell below the minimum price of ${effectiveTp.toFixed(
          2
        )} BDT!`,
        "error"
      );
      setInputValue(effectiveTp);
      dispatch(
        customPriceReducer({
          id: sc,
          article_code: article_code,
          mrp: effectiveTp,
        })
      );
    } else {
      if (num > effectiveTp) {
        originalMrpRef.current = num;
      }
      dispatch(
        customPriceReducer({
          id: sc,
          article_code: article_code,
          mrp: num,
        })
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    } else if (e.key === "ArrowDown" && options.length > 0) {
      setShowDropdown(true);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleSelectBatch = (selected) => {
    if (selected) {
      setSelectCategory(selected.option);
      setInputValue(selected.mrp);
      if (selected.mrp) {
        originalMrpRef.current = selected.mrp;
      }
      if (handleOnChangeCategory) {
        handleOnChangeCategory(sc, selected, article_code);
      } else {
        dispatch(
          customPriceReducer({
            id: sc,
            article_code: article_code,
            e: selected,
          })
        );
      }
    }
  };

  const hoverTooltip =
    effectiveTp > 0
      ? `Cannot sell below the minimum price of ${effectiveTp.toFixed(2)} BDT`
      : "Sale Price";

  if (!editable) {
    return (
      <div
        className="text-center fw-bold"
        style={{
          fontSize: compact ? "13px" : "14px",
          color: "#212529",
          padding: "4px 0",
        }}
        title="Price"
      >
        {setVal !== undefined && setVal !== null ? setVal : ""}
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="d-flex flex-column align-items-center justify-content-center w-100 position-relative"
      style={{ width: "100%" }}
      title={hoverTooltip}
    >
      <style>{`
        .pos-price-input::-webkit-inner-spin-button,
        .pos-price-input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .pos-price-input {
          -moz-appearance: textfield;
        }
      `}</style>

      <div className="position-relative w-100" style={{ width: "100%" }}>
        <input
          type="number"
          className="form-control form-control-sm text-center pos-price-input"
          style={{
            fontWeight: "700",
            fontSize: compact ? "15px" : "14px",
            height: compact ? "30px" : "32px",
            textAlign: "center",
            paddingLeft: compact
              ? options && options.length > 0
                ? "4px"
                : "4px"
              : options && options.length > 0
                ? "26px"
                : "8px",
            paddingRight: compact
              ? options && options.length > 0
                ? "18px"
                : "4px"
              : options && options.length > 0
                ? "26px"
                : "8px",
            backgroundColor: "#fff",
            borderColor: showDropdown ? "#86b7fe" : "#ced4da",
            boxShadow: showDropdown
              ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
              : "none",
          }}
          step="any"
          min={effectiveTp > 0 ? effectiveTp : 0}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          title={hoverTooltip}
        />

        {Array.isArray(options) && options.length > 0 && (
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowDropdown((prev) => !prev);
            }}
            title="Select batch / price"
            style={{
              position: "absolute",
              right: "2px",
              top: "2px",
              bottom: "2px",
              width: compact ? "15px" : "24px",
              background: "transparent",
              border: "none",
              outline: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#6c757d",
              padding: 0,
              borderRadius: "0 4px 4px 0",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#212529")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#6c757d")}
          >
            <svg
              width={compact ? "9" : "11"}
              height={compact ? "9" : "11"}
              viewBox="0 0 16 16"
              fill="currentColor"
              style={{
                transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease",
              }}
            >
              <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
            </svg>
          </button>
        )}
      </div>

      {showDropdown && options && options.length > 0 && (
        <ul
          className="dropdown-menu show shadow-sm"
          style={{
            position: "absolute",
            top: compact ? "30px" : "34px",
            left: 0,
            zIndex: 1060,
            minWidth: "210px",
            maxWidth: "280px",
            maxHeight: "180px",
            overflowY: "auto",
            overflowX: "hidden",
            fontSize: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {options.map((opt) => (
            <li key={opt.option}>
              <button
                type="button"
                className={`dropdown-item py-1 px-2 ${selectCategory === opt.option ||
                  parseFloat(inputValue) === parseFloat(opt.mrp)
                  ? "active"
                  : ""
                  }`}
                style={{ fontSize: "12px", cursor: "pointer" }}
                onClick={() => {
                  handleSelectBatch(opt);
                  setShowDropdown(false);
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="text-truncate me-2"
                    title={opt.supplierName ? opt.supplierName : ""}
                  >
                    <b>৳{opt.mrp}</b>{" "}
                    {opt.supplierName ? `- ${opt.supplierName}` : ""}
                  </span>
                  {opt.stock !== undefined && (
                    <small className="ms-1 opacity-75 flex-shrink-0">
                      [{opt.stock}]
                    </small>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* MRP & TP Toggle Switch */}
      {compact ? (
        <div
          className="d-flex align-items-center justify-content-center gap-1 mt-1 w-100 text-center"
          style={{
            fontSize: "9px",
            lineHeight: "1.2",
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleToggleMode(false);
            }}
            style={{
              cursor: "pointer",
              fontWeight: !isTpMode ? "700" : "500",
              color: !isTpMode ? "#0d6efd" : "#6c757d",
            }}
            title={`MRP`}
          >
            M:<b>৳{effectiveMrp % 1 === 0 ? effectiveMrp.toFixed(0) : effectiveMrp.toFixed(0)}</b>
          </span>
          <div
            role="button"
            tabIndex={0}
            onClick={handleToggleSwitch}
            style={{
              width: "24px",
              height: "14px",
              backgroundColor: isTpMode ? "#198754" : "#0d6efd",
              borderRadius: "8px",
              padding: "1px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              transition: "background-color 0.2s ease",
              flexShrink: 0,
            }}
            title={isTpMode ? "TP active. Toggle to MRP" : "MRP active. Toggle to TP"}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                transform: isTpMode ? "translateX(10px)" : "translateX(0px)",
                transition: "transform 0.2s ease",
              }}
            />
          </div>
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleToggleMode(true);
            }}
            style={{
              cursor: "pointer",
              fontWeight: isTpMode ? "700" : "500",
              color: isTpMode ? "#198754" : "#6c757d",
            }}
            title={`TP`}
          >
            T:<b>৳{effectiveTp % 1 === 0 ? effectiveTp.toFixed(0) : effectiveTp.toFixed(0)}</b>
          </span>
        </div>
      ) : (
        <div
          className="d-flex align-items-center justify-content-center gap-2 mt-1 w-100 text-center"
          style={{
            fontSize: "11px",
            lineHeight: "1.2",
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          {/* MRP Label */}
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleToggleMode(false);
            }}
            style={{
              cursor: "pointer",
              fontWeight: !isTpMode ? "700" : "500",
              color: !isTpMode ? "#0d6efd" : "#6c757d",
              transition: "color 0.15s ease",
            }}
            title={`Click to set MRP: ৳${effectiveMrp}`}
          >
            MRP
          </span>

          {/* Toggle Switch */}
          <div
            role="button"
            tabIndex={0}
            onClick={handleToggleSwitch}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleToggleSwitch(e);
              }
            }}
            style={{
              width: "32px",
              height: "17px",
              backgroundColor: isTpMode ? "#198754" : "#0d6efd",
              borderRadius: "10px",
              padding: "2px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              transition: "background-color 0.2s ease",
              position: "relative",
              flexShrink: 0,
              verticalAlign: "middle",
            }}
            title={
              isTpMode
                ? `Currently TP (৳${effectiveTp}). Click to toggle to MRP (৳${effectiveMrp})`
                : `Currently MRP (৳${effectiveMrp}). Click to toggle to TP (৳${effectiveTp})`
            }
          >
            <div
              style={{
                width: "13px",
                height: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                transform: isTpMode ? "translateX(15px)" : "translateX(0px)",
                transition: "transform 0.2s ease",
              }}
            />
          </div>

          {/* TP Label */}
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleToggleMode(true);
            }}
            style={{
              cursor: "pointer",
              fontWeight: isTpMode ? "700" : "500",
              color: isTpMode ? "#198754" : "#6c757d",
              transition: "color 0.15s ease",
            }}
            title={`Click to set TP: ৳${effectiveTp}`}
          >
            TP
          </span>
        </div>
      )}
    </div>
  );
};

export default PriceSelectByProduct;
